import { Box, Typography } from '@iheartjane/reefer';
import { useEffect, useRef, useState } from 'react';

import { GroupDiscountButton } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerSpecials/groupDiscounts/groupDiscountButton';
import { GroupDiscountDetails } from '@jane/monolith/components/cart/pendingCartDrawer/cartDrawerSpecials/groupDiscounts/groupDiscountDetails';
import { isEligibleForGroupDiscount } from '@jane/monolith/utils/userGroupDiscount';
import { useCheckout } from '@jane/shared-ecomm/providers';
/* eslint-disable-next-line no-restricted-imports */
import type { Store as ModelsStore } from '@jane/shared/models';
import { useHeadlessApp } from '@jane/shared/providers';

import styles from './CheckoutGroupDiscounts.module.css';

export const CheckoutGroupDiscounts = () => {
  const { options } = useHeadlessApp();
  const {
    cart: { group_special_id },
    customer: { authenticated },
    dispatches: { updateGroupSpecial },
    groupSpecials,
    identification: { document },
    store,
  } = useCheckout();

  const [selectedSpecialId, setSelectedSpecialId] = useState<
    string | number | undefined
  >(group_special_id ?? undefined);
  const [eligible, setEligible] = useState(false);

  const eligibleRef = useRef(false);
  const firstRenderRef = useRef(true);

  // Sync local state when cart's group_special_id changes externally
  useEffect(() => {
    setSelectedSpecialId(group_special_id ?? undefined);
  }, [group_special_id]);

  // Re-check eligibility when document or selection changes
  useEffect(() => {
    const selectedSpecial = groupSpecials.find(
      (sp) => sp.id === selectedSpecialId
    );
    const isEligible = isEligibleForGroupDiscount({
      document,
      special: selectedSpecial,
      timezone: store.time_zone_identifier,
      store: {
        medical: store.medical,
        state: store.state,
      },
    });
    setEligible(isEligible);
    eligibleRef.current = isEligible;
  }, [document, selectedSpecialId, groupSpecials, store]);

  // Apply or remove discount when eligibility changes (skip first render)
  useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }

    const specialIdToApply =
      eligibleRef.current && selectedSpecialId
        ? Number(selectedSpecialId)
        : undefined;
    updateGroupSpecial?.(specialIdToApply);
  }, [selectedSpecialId, eligible, updateGroupSpecial]);

  const handleSelectSpecial = (specialId?: string | number) => {
    setSelectedSpecialId(specialId);
    if (specialId === undefined) {
      updateGroupSpecial?.(undefined);
    }
  };

  const selectedDiscount = groupSpecials.find(
    (d) => selectedSpecialId === d.id
  );

  if (
    !options?.showGroupDiscountsAtCheckout ||
    !authenticated ||
    groupSpecials.length === 0
  ) {
    return null;
  }

  const specialsLabel = store.store_compliance_language_settings?.['specials'];

  return (
    <div className={styles['container']}>
      <Typography variant="header-bold" branded mb={24}>
        Apply group {specialsLabel || 'discount'}
      </Typography>
      <Box>
        <GroupDiscountButton
          groupDiscounts={groupSpecials}
          selectedSpecial={selectedSpecialId}
          selectSpecial={handleSelectSpecial}
        />
        {selectedDiscount && (
          <GroupDiscountDetails
            special={selectedDiscount}
            timezone={store.time_zone_identifier}
            store={store as unknown as ModelsStore}
          />
        )}
      </Box>
    </div>
  );
};
